import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["netAmount", "vatAmount", "totalAmount", "vatLine", "validationMessage", "submitButton", "unsavedIndicator", "currencyLabel"]
  static values = { failedSubmission: Boolean }

  connect() {
    this.validateSums()
    this.bindKeyboardShortcut()
    this.setupUnsavedChangesTracking()
  }

  disconnect() {
    this.unbindKeyboardShortcut()
    this.cleanupUnsavedChangesTracking()
  }

  setupUnsavedChangesTracking() {
    // Store initial form state
    this.storeInitialFormState()
    if (this.failedSubmissionValue) {
      this.restoredFailedSubmission = true
      this.hasUnsavedChanges = true
      this.updateUnsavedIndicator()
    }

    // Track all input changes
    this.inputChangeHandler = this.onInputChange.bind(this)
    this.element.addEventListener('input', this.inputChangeHandler)
    this.element.addEventListener('change', this.inputChangeHandler)

    this.companyContextHandler = this.onCompanyContextChange.bind(this)
    document.addEventListener('click', this.companyContextHandler, true)
    document.addEventListener('submit', this.companyContextHandler, true)
    this.turboBeforeVisitHandler = this.onTurboBeforeVisit.bind(this)
    this.turboSubmitEndHandler = this.onTurboSubmitEnd.bind(this)
    this.pageShowHandler = this.onPageShow.bind(this)
    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    document.addEventListener('turbo:submit-end', this.turboSubmitEndHandler)
    window.addEventListener('pageshow', this.pageShowHandler)

    // Warn before leaving page with unsaved changes
    this.beforeUnloadHandler = (event) => {
      if (this.hasUnsavedChanges && !this.submissionInProgress) {
        event.preventDefault()
        event.returnValue = ''
        return ''
      }
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  cleanupUnsavedChangesTracking() {
    document.removeEventListener('click', this.companyContextHandler, true)
    document.removeEventListener('submit', this.companyContextHandler, true)
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
    if (this.inputChangeHandler) {
      this.element.removeEventListener('input', this.inputChangeHandler)
      this.element.removeEventListener('change', this.inputChangeHandler)
    }
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    }
    if (this.turboSubmitEndHandler) {
      document.removeEventListener('turbo:submit-end', this.turboSubmitEndHandler)
    }
    if (this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler)
    }
  }

  storeInitialFormState() {
    const formData = new FormData(this.element)
    this.initialFormState = new Map()

    for (let [key, value] of formData.entries()) {
      this.initialFormState.set(key, value)
    }

    this.hasUnsavedChanges = false
    this.updateUnsavedIndicator()
  }

  onInputChange() {
    if (this.hasCurrencyLabelTarget) {
      this.currencyLabelTarget.textContent = this.element.querySelector('[name="invoice[currency]"]').value
    }
    this.checkForUnsavedChanges()
  }

  checkForUnsavedChanges() {
    const currentFormData = new FormData(this.element)
    let hasChanges = false

    // Check if any values changed
    for (let [key, value] of currentFormData.entries()) {
      const initialValue = this.initialFormState.get(key) || ''
      if (value !== initialValue) {
        hasChanges = true
        break
      }
    }

    // Also check if any initial keys are missing (removed fields)
    if (!hasChanges) {
      for (let [key, value] of this.initialFormState.entries()) {
        if (!currentFormData.has(key) && value !== '') {
          hasChanges = true
          break
        }
      }
    }

    this.hasUnsavedChanges = hasChanges || this.restoredFailedSubmission
    this.updateUnsavedIndicator()
  }

  updateUnsavedIndicator() {
    if (!this.hasUnsavedChanges) {
      const message = document.getElementById('invoice-company-context-message')
      if (message) {
        message.classList.add('hidden')
        message.textContent = ''
      }
    }
    if (this.hasUnsavedIndicatorTarget) {
      if (this.hasUnsavedChanges) {
        this.unsavedIndicatorTarget.classList.remove('hidden')
      } else {
        this.unsavedIndicatorTarget.classList.add('hidden')
      }
    }
  }

  onCompanyContextChange(event) {
    const control = event.target.closest('[data-invoice-company-context]')
    if (!control || !this.shouldConfirmNavigation()) return
    if (event.type === 'click' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) return

    // Changing perspective reloads the form. Keep edits in place until the
    // user saves them rather than submitting an unrelated ownership form.
    event.preventDefault()
    event.stopImmediatePropagation()
    const message = document.getElementById('invoice-company-context-message')
    if (message) {
      message.classList.remove('hidden')
      message.textContent = 'Išsaugokite pakeitimus prieš keisdami administruojamą įmonę.'
      message.scrollIntoView({ block: 'nearest' })
    }
  }

  onTurboBeforeVisit(event) {
    if (!this.shouldConfirmNavigation()) return

    if (!window.confirm(this.unsavedChangesMessage)) {
      event.preventDefault()
    }
  }

  onTurboSubmitEnd(event) {
    if (event.detail.formSubmission?.formElement !== this.element || event.detail.success) return

    this.submissionInProgress = false
    this.checkForUnsavedChanges()
  }

  onPageShow(event) {
    if (event.persisted) this.submissionInProgress = false
  }

  shouldConfirmNavigation() {
    return this.hasUnsavedChanges && !this.submissionInProgress
  }

  get unsavedChangesMessage() {
    return "Turite neišsaugotų pakeitimų. Ar tikrai norite išeiti jų neišsaugoję?"
  }

  bindKeyboardShortcut() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)
  }

  unbindKeyboardShortcut() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  handleKeydown(event) {
    // Check for Cmd+S (Mac) or Ctrl+S (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 's') {
      event.preventDefault()

      // Trigger form submit via the submit button
      if (this.hasSubmitButtonTarget && !this.submitButtonTarget.disabled) {
        this.submitButtonTarget.click()
      }
    }
  }

  validateSums() {
    if (!this.hasValidationMessageTarget) return
    const lines = this.vatLineTargets.filter(line => !this.isMarkedForDestroy(line))
    const calculated = this.calculateVatLineSums(lines)
    const invoice = this.getInvoiceAmounts()
    const differs = lines.length > 0 && Object.keys(invoice).some(key =>
      Math.abs(calculated[key] - invoice[key]) > 0.02)
    this.validationMessageTarget.textContent = differs
      ? "Suvestinės ir sąskaitos sumos skiriasi. Išsaugomos įvestos reikšmės."
      : ""
    this.validationMessageTarget.classList.toggle("hidden", !differs)
    this.validationMessageTarget.classList.add("text-gray-600")
  }

  calculateVatLineSums(vatLines) {
    let netSum = 0
    let vatSum = 0
    let totalSum = 0

    vatLines.forEach(line => {
      const netInput = line.querySelector('input[name*="[net_amount]"]')
      const vatInput = line.querySelector('input[name*="[vat_amount]"]')
      const totalInput = line.querySelector('input[name*="[total_amount]"]')

      if (netInput && netInput.value) netSum += parseFloat(netInput.value) || 0
      if (vatInput && vatInput.value) vatSum += parseFloat(vatInput.value) || 0
      if (totalInput && totalInput.value) totalSum += parseFloat(totalInput.value) || 0
    })

    return { net: netSum, vat: vatSum, total: totalSum }
  }

  getInvoiceAmounts() {
    return {
      net: parseFloat(this.netAmountTarget.value) || 0,
      vat: parseFloat(this.vatAmountTarget.value) || 0,
      total: parseFloat(this.totalAmountTarget.value) || 0
    }
  }

  isMarkedForDestroy(vatLine) {
    const destroyInput = vatLine.querySelector('input[name*="[_destroy]"]')
    return destroyInput && destroyInput.value === "1"
  }

  // Event handlers for input changes
  onInvoiceAmountChange() {
    this.validateSums()
  }

  onVatLineChange() {
    this.validateSums()
  }

  onFormSubmit(event) {
    this.submissionInProgress = true

    // Another handler can still cancel submission after this Stimulus action.
    // In that case the form remains dirty and navigation protection must stay.
    queueMicrotask(() => {
      if (event.defaultPrevented) this.submissionInProgress = false
    })

    return true
  }
}
